import { createContext, useContext, useEffect, useState } from "react";
import { sb } from "@/lib/supabase";

const AuthCtx = createContext({ user: null, loading: true, login: () => {}, logout: () => {} });

function apiErr(detail) {
  const e = new Error(detail);
  e.response = { status: 400, data: { detail } };
  return e;
}

// Skontroluj dash_access v profiles — dashboard len pre povolené účty
async function checkDashAccess(userId) {
  const { data, error } = await sb
    .from("profiles")
    .select("dash_access")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw apiErr(error.message);
  return !!data?.dash_access;
}

const toUser = (u) => ({
  id: u.id,
  email: u.email,
  name: u.user_metadata?.name || u.email?.split("@")[0] || "Creator",
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await sb.auth.getSession();
        const su = data?.session?.user;
        if (su && (await checkDashAccess(su.id))) setUser(toUser(su));
      } catch (_) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data, error } = await sb.auth.signInWithPassword({ email, password });
    if (error) throw apiErr("Nesprávny e-mail alebo heslo");
    const hasAccess = await checkDashAccess(data.user.id);
    if (!hasAccess) {
      await sb.auth.signOut();
      throw apiErr("Tento účet nemá prístup k Creator Dashboardu");
    }
    const u = toUser(data.user);
    setUser(u);
    return u;
  };

  const register = async (email, password, name) => {
    const { data, error } = await sb.auth.signUp({
      email, password,
      options: { data: { name: name || email.split("@")[0] } },
    });
    if (error) throw apiErr(error.message || "Registrácia zlyhala");
    // Ak Supabase vyžaduje potvrdenie e-mailu, session je null
    if (!data.session) {
      return { needsConfirm: true };
    }
    // dash_access má default true (nastavené v SQL) → over
    const hasAccess = await checkDashAccess(data.user.id);
    if (!hasAccess) {
      await sb.auth.signOut();
      throw apiErr("Účet vytvorený, ale nemá prístup k dashboardu — kontaktuj podporu");
    }
    setUser(toUser(data.user));
    return { needsConfirm: false };
  };

  const logout = async () => {
    await sb.auth.signOut();
    setUser(null);
    window.location.href = "/login";
  };

  return <AuthCtx.Provider value={{ user, loading, login, register, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
